import Phaser from "phaser";
import { artTextureKey, isArtLoaded } from "@/sprites/artManifest";
import { slotRect, type SceneVisuals } from "@/data/visuals";
import type { GameState } from "@/state/GameState";

/**
 * buildStage (v0.9 RFC §7): render a scene's visuals table against the
 * current state. Pure application of data — no slot logic lives in scenes.
 *
 * Contract:
 *  - a slot renders iff (no predicate OR predicate(state)) AND its art is
 *    loaded (manifest graceful-fallback; a missing cut skips silently at
 *    runtime — the visuals TEST is what fails loud on unknown keys).
 *  - re-entrant: call again on state change; it diffs by slot key, adding
 *    newly-true slots and destroying newly-false ones (prestige, purchases).
 */

export class StageLayer {
  private readonly scene: Phaser.Scene;
  private readonly visuals: SceneVisuals;
  /** Keyed by slot INDEX (two slots may legally share a sprite key); the
   * value carries the sprite key for hit-test reporting. */
  private readonly live = new Map<number, { key: string; img: Phaser.GameObjects.Image }>();

  constructor(scene: Phaser.Scene, visuals: SceneVisuals) {
    this.scene = scene;
    this.visuals = visuals;
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  /** Apply the table against state: add newly-true slots, drop newly-false. */
  sync(state: GameState): void {
    this.visuals.slots.forEach((slot, i) => {
      const want = (slot.when?.(state) ?? true) && isArtLoaded(slot.key);
      const have = this.live.get(i);
      if (want && !have) {
        const { x, bottom, h } = slotRect(this.visuals, slot);
        const img = this.scene.add
          .image(x, bottom, artTextureKey(slot.key))
          .setOrigin(0.5, 1); // bottom-center anchor on the baseline
        img.setScale(h / img.height);
        // Depth: floor-local bias on top of the baseline, so rope (z4) beats
        // characters (which use the feet-y convention) beats wall art (z1).
        img.setDepth(bottom + slot.z * 10);
        this.live.set(i, { key: slot.key, img });
      } else if (!want && have) {
        have.img.destroy();
        this.live.delete(i);
      }
    });
  }

  /** Topmost live slot containing the world point (tap-a-prop hit test). */
  hitTest(wx: number, wy: number): string | null {
    let best: { key: string; depth: number } | null = null;
    for (const { key, img } of this.live.values()) {
      if (img.getBounds().contains(wx, wy) && (best === null || img.depth > best.depth)) {
        best = { key, depth: img.depth };
      }
    }
    return best?.key ?? null;
  }

  destroy(): void {
    for (const { img } of this.live.values()) img.destroy();
    this.live.clear();
  }
}
